/**
 * IndexedDB implementation of IBookStorage
 * Uses browser's IndexedDB API for persistence
 */

import type { Book } from '../../../contracts/book.ts';
import type { IBookStorage, StorageInfo } from '../../../contracts/storage.ts';
import { IndexedDBWrapper } from './indexeddb-wrapper.ts';

const STORE_NAME = 'books';

export class IndexedDBBookStorage implements IBookStorage {
  private db: IndexedDBWrapper;

  constructor(db?: IndexedDBWrapper) {
    this.db = db || new IndexedDBWrapper();
  }

  async addBook(book: Book): Promise<void> {
    await this.db.put(STORE_NAME, book);
  }

  async getBook(id: string): Promise<Book | null> {
    return await this.db.get<Book>(STORE_NAME, id);
  }

  async getAllBooks(): Promise<Book[]> {
    const books = await this.db.getAll<Book>(STORE_NAME);
    // Sort by createdAt descending (newest first)
    return books.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
  }

  async searchBooks(query: string): Promise<Book[]> {
    const allBooks = await this.getAllBooks();
    const lowerQuery = query.toLowerCase();

    return allBooks.filter((book) => {
      return (
        book.title.toLowerCase().includes(lowerQuery) ||
        book.metadata.author?.toLowerCase().includes(lowerQuery) ||
        book.metadata.description?.toLowerCase().includes(lowerQuery) ||
        book.metadata.tags?.some((tag) =>
          tag.toLowerCase().includes(lowerQuery)
        )
      );
    });
  }

  async updateBook(id: string, updates: Partial<Book>): Promise<void> {
    const existing = await this.getBook(id);

    if (!existing) {
      throw new Error(`Book with id ${id} not found`);
    }

    const updated: Book = {
      ...existing,
      ...updates,
      id: existing.id, // Prevent ID changes
      updatedAt: new Date(),
    };

    await this.db.put(STORE_NAME, updated);
  }

  async deleteBook(id: string): Promise<void> {
    await this.db.delete(STORE_NAME, id);
  }

  async getStorageInfo(): Promise<StorageInfo> {
    const { usage, quota } = await this.db.estimateQuota();

    const percentage = quota > 0 ? (usage / quota) * 100 : 0;

    return {
      used: usage,
      total: quota || 0,
      percentage: Math.round(percentage * 100) / 100,
    };
  }
}
